import type { Action } from '../../../bridge/actions';
import type { Space, UserState } from '../../../bridge/types/state.ts';

export const SPACE_TYPES = [
	'campaigns',
	'brand',
	'content',
	'contacts',
	'audiences',
	'settings',
] as const satisfies readonly Space['type'][];

export type SpaceType = (typeof SPACE_TYPES)[number];

export const isSpaceType = (value: string): value is SpaceType =>
	(SPACE_TYPES as readonly string[]).includes(value);

export type ParsedRoute =
	| { kind: 'chat-root' }
	| { kind: 'chat' }
	| { kind: 'space'; space: SpaceType }
	| { kind: 'content-detail'; artifactId: string }
	| { kind: 'chat-thread'; chatId: string }
	| { kind: 'unknown' };

export const parsePathname = (pathname: string): ParsedRoute => {
	if (pathname === '/' || pathname === '') {
		return { kind: 'chat-root' };
	}
	const segments = pathname.split('/').filter(Boolean);
	if (segments.length === 1 && segments[0] === 'chat') {
		return { kind: 'chat' };
	}
	if (segments.length === 1 && isSpaceType(segments[0])) {
		return { kind: 'space', space: segments[0] };
	}
	if (segments.length === 2 && segments[0] === 'content' && segments[1]) {
		return { kind: 'content-detail', artifactId: segments[1] };
	}
	if (segments.length === 2 && segments[0] === 'chat' && segments[1]) {
		return { kind: 'chat-thread', chatId: segments[1] };
	}
	return { kind: 'unknown' };
};

// The one URL that describes a given view state. The view is fully determined
// by current_space and current_chat_id: a space renders at /<type> (content
// additionally exposes its open detail page), and no space means the chat
// view, at /chat/<id> once a chat exists. Returns null while there is no
// server state to describe (socket still connecting).
//
// Contact/audience detail selections deliberately have no URL: PeopleSpace
// reads selected_contact_id/selected_audience_id only as useState initializers,
// so a URL-driven action could not change the selection of an already-mounted
// PeopleSpace. Giving those ids URLs needs a PeopleSpace refactor first
// (follow-up ticket).
export const derivePathFromState = (
	userState: UserState | null,
): string | null => {
	if (!userState) {
		return null;
	}
	const space = userState.current_space;
	if (space) {
		if (space.type === 'content' && space.artifact_id) {
			return `/content/${space.artifact_id}`;
		}
		return `/${space.type}`;
	}
	if (userState.current_chat_id) {
		return `/chat/${userState.current_chat_id}`;
	}
	return '/';
};

// The actions needed to make the view state match a URL — and nothing more.
// Every action here is a real state change; anything already true is skipped.
// The skipping matters beyond economy: a redundant do_switch_chat must never
// disturb the user's open side panel. The reducer now also leaves the panel
// alone when the target chat is already the current one, so this skip is the
// outer of two deliberate layers protecting it — keep both.
export const diffRouteActions = (
	parsed: ParsedRoute,
	userState: UserState,
	{ isInitial }: { isInitial: boolean },
): Action[] => {
	const actions: Action[] = [];
	const space = userState.current_space ?? null;

	switch (parsed.kind) {
		case 'space': {
			if (!space || space.type !== parsed.space) {
				actions.push({
					type: 'do_navigate_to_space',
					space_type: parsed.space,
				});
			} else if (space.type === 'content' && space.artifact_id) {
				// /content with a detail page open: close the detail page. Re-entering
				// the space would be a no-op since we're already in it.
				actions.push({
					type: 'do_navigate_to_content_detail_page',
					artifact_id: null,
				});
			}
			break;
		}
		case 'content-detail': {
			const currentArtifactId =
				space?.type === 'content' ? (space.artifact_id ?? null) : null;
			if (space?.type !== 'content') {
				actions.push({
					type: 'do_navigate_to_space',
					space_type: 'content',
				});
			}
			if (currentArtifactId !== parsed.artifactId) {
				actions.push({
					type: 'do_navigate_to_content_detail_page',
					artifact_id: parsed.artifactId,
				});
			}
			break;
		}
		case 'chat-thread': {
			if (space) {
				actions.push({ type: 'do_navigate_to_space', space_type: 'chat' });
			}
			if (userState.current_chat_id !== parsed.chatId) {
				actions.push({ type: 'do_switch_chat', chat_id: parsed.chatId });
			}
			break;
		}
		case 'chat': {
			// /chat means "the current chat" — leaving a space is enough; the
			// current chat is already current.
			if (space) {
				actions.push({ type: 'do_navigate_to_space', space_type: 'chat' });
			}
			break;
		}
		case 'chat-root': {
			// On a cold load, / is the neutral entry point: whatever view the
			// server persisted (a space survives reloads via Redis) should render,
			// and the URL gets normalized to it afterwards. Only an explicit
			// back/forward to / means "go to chat".
			if (!isInitial && space) {
				actions.push({ type: 'do_navigate_to_space', space_type: 'chat' });
			}
			break;
		}
		case 'unknown':
			// Nothing to converge to; the URL gets rewritten to the state's path.
			break;
	}

	return actions;
};
